import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import ItemCard from '../components/ItemCard.jsx'
import WeaponArt from '../components/WeaponArt.jsx'
import CoinIcon from '../components/CoinIcon.jsx'
import {
  formatRub,
  formatDate,
  formatFloat,
  plural,
  sourceLabel,
  RARITY_LABELS,
  RARITY_COLORS,
  WEAR_SHORT,
} from '../lib/format.js'
import '../styles/inventory.css'

const SORTS = [
  { id: 'price-desc', label: 'Дороже' },
  { id: 'price-asc', label: 'Дешевле' },
  { id: 'new', label: 'Новее' },
]

export default function Inventory() {
  const { user, setBalance } = useAuth()
  const toast = useToast()

  const [tab, setTab] = useState('items') // items | withdrawals | history
  const [data, setData] = useState(null)
  const [history, setHistory] = useState(null)
  const [withdrawals, setWithdrawals] = useState(null)
  const [selected, setSelected] = useState(new Set())
  const [sort, setSort] = useState('price-desc')
  const [rarity, setRarity] = useState('all')
  const [busy, setBusy] = useState(false)

  // Вывод скина через Market.CSGO
  const [withdrawItem, setWithdrawItem] = useState(null)
  const [withdrawTradeUrl, setWithdrawTradeUrl] = useState('')
  const [withdrawBusy, setWithdrawBusy] = useState(false)
  const [withdrawError, setWithdrawError] = useState(null)
  const [withdrawResult, setWithdrawResult] = useState(null)

  function reload() {
    if (!user) return
    api.inventory().then(setData).catch((err) => toast.error(err.message))
  }

  function reloadWithdrawals() {
    if (!user) return
    api.withdrawals().then(({ withdrawals: list }) => setWithdrawals(list)).catch(() => setWithdrawals([]))
  }

  useEffect(() => {
    if (user) {
      reload()
      if (user.tradeUrl) {
        setWithdrawTradeUrl(user.tradeUrl)
      } else {
        api.getTradeUrl().then(({ tradeUrl: url }) => {
          if (url) setWithdrawTradeUrl(url)
        }).catch(() => {})
      }
    }
  }, [user])

  useEffect(() => {
    if (tab === 'history' && history === null) {
      api.inventoryHistory().then(({ items }) => setHistory(items)).catch(() => setHistory([]))
    }
    if (tab === 'withdrawals' && withdrawals === null) {
      reloadWithdrawals()
    }
  }, [tab, history, withdrawals])

  const items = data?.items ?? null

  const rarities = useMemo(() => {
    if (!items) return []
    const counts = {}
    for (const item of items) counts[item.rarity] = (counts[item.rarity] ?? 0) + 1
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
  }, [items])

  const visible = useMemo(() => {
    if (!items) return []
    const filtered = rarity === 'all' ? items : items.filter((i) => i.rarity === rarity)
    const copy = [...filtered]
    if (sort === 'price-desc') copy.sort((a, b) => b.priceKopecks - a.priceKopecks)
    if (sort === 'price-asc') copy.sort((a, b) => a.priceKopecks - b.priceKopecks)
    if (sort === 'new') copy.sort((a, b) => b.id - a.id)
    return copy
  }, [items, rarity, sort])

  const selectedList = useMemo(
    () => (items ?? []).filter((item) => selected.has(item.id)),
    [items, selected],
  )

  const selectedPayout = selectedList.reduce((sum, item) => sum + item.sellKopecks, 0)

  function toggle(id) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllVisible() {
    setSelected(new Set(visible.map((item) => item.id)))
  }

  async function sellSelected() {
    if (selectedList.length === 0 || busy) return
    setBusy(true)
    try {
      const response = await api.sell([...selected])
      setBalance(response.balance)
      toast.success(
        `Продано ${response.sold.length} ${plural(response.sold.length, 'предмет', 'предмета', 'предметов')} за ${formatRub(response.payoutKopecks)}`,
      )
      setSelected(new Set())
      setHistory(null)
      reload()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function sellEverything() {
    if (busy || !items?.length) return
    setBusy(true)
    try {
      const response = await api.sellAll()
      setBalance(response.balance)
      toast.success(`Инвентарь продан за ${formatRub(response.payoutKopecks)}`)
      setSelected(new Set())
      setHistory(null)
      reload()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBusy(false)
    }
  }

  function openWithdraw(item) {
    setWithdrawItem(item)
    setWithdrawError(null)
    setWithdrawResult(null)
  }

  function closeWithdraw() {
    setWithdrawItem(null)
    setWithdrawError(null)
    setWithdrawResult(null)
  }

  async function confirmWithdraw() {
    if (!withdrawItem || withdrawBusy) return
    if (!withdrawTradeUrl.trim()) {
      setWithdrawError('Укажите вашу ссылку на обмен Steam (Trade URL)')
      return
    }

    setWithdrawBusy(true)
    setWithdrawError(null)
    try {
      const response = await api.withdraw(withdrawItem.id, withdrawTradeUrl.trim())
      setWithdrawResult(response)
      toast.success('Запрос на вывод отправлен на Market.CSGO!')
      reload()
      if (withdrawals !== null) reloadWithdrawals()
    } catch (err) {
      setWithdrawError(err.message)
    } finally {
      setWithdrawBusy(false)
    }
  }

  return (
    <div className="page container inventory-page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Склад</span>
          <h1>Инвентарь</h1>
          <p>
            Предметы можно продать обратно, отправить в контракт или поставить в
            апгрейд. Комиссия при продаже —{' '}
            {data ? `${(data.sellFee * 100).toFixed(0)}%` : '10%'}.
          </p>
        </div>

        {data && (
          <div className="inv-total card card-pad">
            <span className="tiny muted">Всего в инвентаре</span>
            <span className="inv-total-value" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <span>{formatRub(data.totalKopecks)}</span>
              <CoinIcon size="md" />
            </span>
            <span className="tiny muted">
              {items.length} {plural(items.length, 'предмет', 'предмета', 'предметов')}
            </span>
          </div>
        )}
      </div>

      <div className="seg">
        <button
          type="button"
          className={`seg-btn ${tab === 'items' ? 'is-active' : ''}`}
          onClick={() => setTab('items')}
        >
          Предметы
        </button>
        <button
          type="button"
          className={`seg-btn ${tab === 'withdrawals' ? 'is-active' : ''}`}
          onClick={() => setTab('withdrawals')}
        >
          Выводы (Market.CSGO)
        </button>
        <button
          type="button"
          className={`seg-btn ${tab === 'history' ? 'is-active' : ''}`}
          onClick={() => setTab('history')}
        >
          История
        </button>
      </div>

      {tab === 'items' ? (
        !user ? (
          <div className="card card-pad center inv-empty">
            <h3>Инвентарь доступен после входа</h3>
            <p className="muted">
              Войди через Steam, чтобы просматривать свои выбитые скины, продавать их или использовать в крафтах и апгрейдах.
            </p>
            <a href="/api/auth/steam" className="btn btn-steam btn-lg">
              <svg className="steam-icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.005.105.005.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 12-5.373 12-12S18.605 0 11.979 0z" />
              </svg>
              <span>Войти через Steam</span>
            </a>
          </div>
        ) : items === null ? (
          <div className="inv-grid">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 210 }} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="card card-pad center inv-empty">
            <h3>Пока пусто</h3>
            <p className="muted">Открой кейс — и первый скин появится здесь.</p>
            <Link to="/cases" className="btn btn-primary">
              К кейсам
            </Link>
          </div>
        ) : (
          <>
            <div className="inv-toolbar">
              <div className="quick-row" style={{ margin: 0 }}>
                <button
                  type="button"
                  className={`chip ${rarity === 'all' ? 'is-active' : ''}`}
                  onClick={() => setRarity('all')}
                >
                  Все · {items.length}
                </button>
                {rarities.map(([id, count]) => (
                  <button
                    key={id}
                    type="button"
                    className={`chip ${rarity === id ? 'is-active' : ''}`}
                    onClick={() => setRarity(id)}
                    style={rarity === id ? undefined : { borderColor: RARITY_COLORS[id] }}
                  >
                    {RARITY_LABELS[id]} · {count}
                  </button>
                ))}
              </div>

              <div className="row">
                <div className="seg" style={{ margin: 0 }}>
                  {SORTS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`seg-btn ${sort === option.id ? 'is-active' : ''}`}
                      onClick={() => setSort(option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="inv-grid">
              {visible.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  showFloat
                  selected={selected.has(item.id)}
                  onClick={() => toggle(item.id)}
                  footer={
                    <span className="inv-card-foot">
                      <button
                        type="button"
                        className="inv-withdraw-quick-btn"
                        title="Вывести этот скин в Steam через Market.CSGO"
                        onClick={(e) => {
                          e.stopPropagation()
                          openWithdraw(item)
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                        <span>Вывести</span>
                      </button>
                      <span className="tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span>продажа</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          <span>{formatRub(item.sellKopecks)}</span>
                          <CoinIcon size="xs" />
                        </span>
                      </span>
                    </span>
                  }
                />
              ))}
            </div>

            {/* Панель массовых действий появляется только когда есть выбор. */}
            <div className={`inv-actions ${selectedList.length > 0 ? 'is-open' : ''}`}>
              <div className="container inv-actions-inner">
                <span className="strong" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>{selectedList.length}{' '}
                  {plural(selectedList.length, 'предмет', 'предмета', 'предметов')} ·</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <span>{formatRub(selectedPayout)}</span>
                    <CoinIcon size="xs" />
                  </span>
                </span>
                <div className="row wrap" style={{ gap: 8 }}>
                  {selectedList.length === 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm inv-withdraw-floating-btn"
                      onClick={() => openWithdraw(selectedList[0])}
                      disabled={busy}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                      <span>Вывести в Steam</span>
                    </button>
                  )}
                  <button type="button" className="btn btn-ghost btn-sm" onClick={selectAllVisible}>
                    Выбрать все
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelected(new Set())}
                  >
                    Снять выбор
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={sellSelected}
                    disabled={busy}
                  >
                    {busy ? (
                      'Продаём…'
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span>Продать за</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          <span>{formatRub(selectedPayout)}</span>
                          <CoinIcon size="xs" />
                        </span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="inv-footer">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={sellEverything}
                disabled={busy}
              >
                Продать весь инвентарь
              </button>
              <Link to="/crafts" className="btn btn-soft">
                В контракты →
              </Link>
            </div>
          </>
        )
      ) : tab === 'withdrawals' ? (
        withdrawals === null ? (
          <div className="skeleton" style={{ height: 320 }} />
        ) : withdrawals.length === 0 ? (
          <div className="card card-pad center inv-empty">
            <h3>Выводов пока не было</h3>
            <p className="muted">
              Выбирай любой скин из своего инвентаря и нажимай «Вывести», чтобы моментально отправить его в Steam через Market.CSGO!
            </p>
          </div>
        ) : (
          <div className="card history-table">
            <div className="history-row history-head" style={{ gridTemplateColumns: 'minmax(200px, 1.8fr) 1.2fr 1fr 1fr 1fr' }}>
              <span>Предмет</span>
              <span>ID Заказа Market.CSGO</span>
              <span>Статус доставки</span>
              <span>Стоимость</span>
              <span>Когда</span>
            </div>
            {withdrawals.map((w) => (
              <div key={w.id} className="history-row" style={{ gridTemplateColumns: 'minmax(200px, 1.8fr) 1.2fr 1fr 1fr 1fr' }}>
                <span className="strong tiny">{w.market_hash_name}</span>
                <span className="tiny muted font-mono">{w.market_order_id || w.custom_id}</span>
                <span className={`pill status-${w.status === 'sent' || w.status === 'completed' ? 'owned' : w.status === 'failed' ? 'consumed' : 'pending'}`}>
                  {w.status === 'sent' ? 'Отправлен в Steam' : w.status === 'completed' ? 'Доставлен' : w.status === 'failed' ? 'Ошибка' : 'В обработке'}
                </span>
                <span className="tiny strong" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  <span>{formatRub(w.price_kopecks)}</span>
                  <CoinIcon size="xs" />
                </span>
                <span className="tiny muted">{formatDate(w.created_at)}</span>
              </div>
            ))}
          </div>
        )
      ) : history === null ? (
        <div className="skeleton" style={{ height: 320 }} />
      ) : history.length === 0 ? (
        <p className="muted center" style={{ padding: 40 }}>
          История пуста.
        </p>
      ) : (
        <div className="card history-table">
          <div className="history-row history-head">
            <span>Предмет</span>
            <span>Износ</span>
            <span>Источник</span>
            <span>Статус</span>
            <span>Цена</span>
            <span>Когда</span>
          </div>
          {history.map((item) => (
            <div key={item.id} className="history-row">
              <span className="history-item">
                <WeaponArt image={item.image} rarity={item.rarity} size="sm" />
                <span className="history-names">
                  <span className="strong tiny">{item.name}</span>
                  <span className="tiny muted">{item.skin}</span>
                </span>
              </span>
              <span className="tiny muted">
                {WEAR_SHORT[item.wear] ?? item.wear} · {formatFloat(item.floatValue)}
              </span>
              <span className="tiny">{sourceLabel(item.source)}</span>
              <span className={`pill status-${item.status}`}>
                {item.status === 'owned'
                  ? 'В инвентаре'
                  : item.status === 'sold'
                    ? 'Продан'
                    : 'Использован'}
              </span>
              <span className="tiny strong" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                <span>{formatRub(item.priceKopecks)}</span>
                <CoinIcon size="xs" />
              </span>
              <span className="tiny muted">{formatDate(item.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Модальное окно вывода через Market.CSGO */}
      {withdrawItem && (
        <div className="withdraw-modal-overlay" onClick={closeWithdraw}>
          <div className="withdraw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="withdraw-modal-head">
              <div className="withdraw-modal-title">
                <span className="eyebrow" style={{ color: 'var(--cyan-400, #38bdf8)' }}>Market.CSGO P2P Delivery</span>
                <h2>Вывод скина в Steam</h2>
              </div>
              <button type="button" className="btn-close" onClick={closeWithdraw}>✕</button>
            </div>

            <div className="withdraw-modal-body">
              {withdrawResult ? (
                <div className="withdraw-success-box">
                  <div className="withdraw-success-icon">✓</div>
                  <h3>Запрос на вывод отправлен!</h3>
                  <p className="muted" style={{ marginTop: 8 }}>
                    {withdrawResult.message || 'Скин отправляется ботом Market.CSGO. Проверьте входящие обмены в Steam!'}
                  </p>
                  {withdrawResult.orderId && (
                    <div className="withdraw-order-chip" style={{ margin: '14px 0' }}>
                      <span className="tiny muted">ID заказа:</span> <code>{withdrawResult.orderId}</code>
                    </div>
                  )}
                  <button type="button" className="btn btn-primary" onClick={closeWithdraw} style={{ marginTop: 12 }}>
                    Отлично
                  </button>
                </div>
              ) : (
                <>
                  <div className="withdraw-item-preview">
                    <WeaponArt image={withdrawItem.image} rarity={withdrawItem.rarity} size="md" />
                    <div className="withdraw-item-info">
                      <span className="strong">{withdrawItem.name} | {withdrawItem.skin}</span>
                      <span className="tiny muted">
                        {RARITY_LABELS[withdrawItem.rarity]} · {withdrawItem.wearLabel || WEAR_SHORT[withdrawItem.wear] || 'Обычный'}
                        {withdrawItem.floatValue !== null && ` · float ${formatFloat(withdrawItem.floatValue)}`}
                      </span>
                      <span className="withdraw-item-price" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                        <span>Стоимость: {formatRub(withdrawItem.priceKopecks)}</span>
                        <CoinIcon size="xs" />
                      </span>
                    </div>
                  </div>

                  <div className="withdraw-form-group">
                    <div className="row wrap justify-between" style={{ marginBottom: 6 }}>
                      <label className="field-label" htmlFor="trade-url-input" style={{ margin: 0 }}>
                        Ссылка на обмен Steam (Trade URL):
                      </label>
                      <a
                        href="https://steamcommunity.com/my/tradeoffers/privacy#trade_offer_access_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tiny link muted"
                      >
                        Где взять ссылку? ↗
                      </a>
                    </div>
                    <input
                      id="trade-url-input"
                      type="text"
                      className="input"
                      placeholder="https://steamcommunity.com/tradeoffer/new/?partner=...&token=..."
                      value={withdrawTradeUrl}
                      onChange={(e) => setWithdrawTradeUrl(e.target.value)}
                    />
                    <span className="field-hint" style={{ marginTop: 6, display: 'block' }}>
                      Market.CSGO купит скин на маркете и отправит P2P-обмен напрямую на ваш Trade URL.
                    </span>
                  </div>

                  {withdrawError && (
                    <div className="alert alert-error" style={{ marginBottom: 16 }}>
                      {withdrawError}
                    </div>
                  )}

                  <div className="withdraw-modal-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={closeWithdraw}
                      disabled={withdrawBusy}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={confirmWithdraw}
                      disabled={withdrawBusy || !withdrawTradeUrl.trim()}
                    >
                      {withdrawBusy ? 'Отправляем в Market.CSGO…' : 'Подтвердить вывод'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
